import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageTemplate from '../components/Common/PageTemplate';
import { BookOpen, Users, Calendar, ExternalLink } from 'lucide-react';
import sonYayinlarData from '../data/SonYayinlar.json';

const PublicationsPage = () => {
    const { t } = useTranslation();
    const [publications, setPublications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPublications = async () => {
            try {
                // JSON dosyasından verileri yükle ve boş kayıtları filtrele
                const filteredData = sonYayinlarData.filter(pub => 
                    pub && 
                    pub.baslik && 
                    pub.baslik.trim() !== '' && 
                    pub.baslik.trim() !== 'undefined' &&
                    pub.abstract && 
                    pub.abstract.trim() !== '' &&
                    pub.abstract.trim() !== '\n'
                );

                // JSON field'larını beklenen field'lara map et
                const mappedData = filteredData.map(pub => ({
                    id: pub.baslik || Math.random(), // ID yoksa baslik kullan
                    title: pub.baslik,
                    authors: pub.yazarlar,
                    abstract: pub.abstract,
                    publication_date: pub.tarih,
                    publication_url: pub.link,
                    image_url: pub.gorsel
                }));

                const sortedData = mappedData.sort((a, b) => {
                    return new Date(b.publication_date) - new Date(a.publication_date);
                });
                setPublications(sortedData);
            } catch (error) {
                console.error("Yayınlar yüklenirken hata oluştu:", error);
            } finally {
                setLoading(false);
            }
        };

        loadPublications();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        if (dateString.includes('-')) {
            const date = new Date(dateString);
            return date.toLocaleDateString('tr-TR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        }
        return dateString; // Eğer zaten Türkçe formatsa olduğu gibi döndür
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const PublicationCard = ({ pub }) => {
        const [showAbstract, setShowAbstract] = useState(false);

        return (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col md:flex-row mb-8 transform transition-transform duration-300 hover:scale-105 hover:shadow-2xl">
                {pub.image_url && (
                    <div className="md:w-1/4">
                        <img src={pub.image_url} alt={pub.title} className="w-full h-full object-cover" />
                    </div>
                )}
                <div className="p-6 flex flex-col justify-between md:w-3/4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">{pub.title}</h2>
                        
                        <div className="text-gray-600 mb-3 flex items-center text-sm">
                            <Users className="w-4 h-4 mr-2 text-gray-500" />
                            <span>{pub.authors}</span>
                        </div>
                        
                        <div className="text-gray-500 mb-4 flex items-center text-sm">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Yayın Tarihi: {formatDate(pub.publication_date)}</span>
                        </div>
                        
                        {pub.abstract && (
                            <>
                                <button 
                                    onClick={() => setShowAbstract(!showAbstract)}
                                    className="text-blue-600 hover:underline text-sm mb-2"
                                >
                                    {showAbstract ? 'Özeti Gizle' : 'Özeti Göster'}
                                </button>
                                {showAbstract && (
                                    <p className="text-gray-700 text-sm mb-4 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                                        {pub.abstract}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                    
                    <a 
                        href={pub.publication_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors self-start"
                    >
                        Yayına Git <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                </div>
            </div>
        );
    };

    return (
        <PageTemplate title={t('pages.publications.title')}>
            <div className="container mx-auto px-4 py-8">
                {loading ? (
                    <div className="text-center">Yayınlar yükleniyor...</div>
                ) : (
                    <div>
                        {publications.map(pub => (
                            <PublicationCard key={pub.id} pub={pub} />
                        ))}
                    </div>
                )}
                 {!loading && publications.length === 0 && (
                    <div className="text-center py-16">
                        <p className="text-gray-500">Gösterilecek yayın bulunamadı.</p>
                    </div>
                )}
            </div>
        </PageTemplate>
    );
};

export default PublicationsPage; 